import {
  SearchPredefinedTask,
  CreatePredefinedTask,
  DeletePredefinedTask,
  UpdatePredefinedTaskType,
  UpdatePredefinedTaskName,
  UpdatePredefinedTaskIcon,
  UpdatePredefinedTaskSuggestedRRULE,
  PredefinedTask,
  CreatePredefinedTaskInput,
  UpdatePredefinedTaskTypeInput,
  UpdatePredefinedTaskNameInput,
  UpdatePredefinedTaskIconInput,
  UpdatePredefinedTaskSuggestedRRULEInput,
  GetPredefinedTask,
  UpdatePredefinedTaskSpecies,
  AddPredefinedTaskSpecies,
  RemovePredefinedTaskSpecies,
  UpdatePredefinedTaskState
} from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Tasks/PathItems";
import { SearchInput } from "../../PetKey/Main/Model/1_0_Initial/Core/PathItems";
import { generateUUID, Result, TransportAbstractionBuilder, TransportContext } from "@peercolab/engine";
import { TranslationAppender } from "./translationMocks";
import { applyStateTransition, transitionState } from "../../../utils/GeneralHelpers";

export function mockPredefinedTasksInterceptors(builder: TransportAbstractionBuilder, appender: TranslationAppender) {
  const dbTasks: PredefinedTask[] = [];
  const dbTasksPublished: PredefinedTask[] = [];

  const getTask = (ctx: string | TransportContext): Result<PredefinedTask> => {
    const id = typeof ctx === 'string' ? ctx : ctx.getPathParameter<string>('predefinedTaskId');
    const task = dbTasks.find(t => t.id === id);
    if (!task) {
      return Result.notFound('Backoffice.Tasks.NotFound', `PredefinedTask with id ${id} not found`);
    }
    return Result.ok(task);
  };

  const saveTask = (result: Result<PredefinedTask>, ctx: TransportContext): Result<PredefinedTask> => {
    if (!result.success) {
      return result;
    }
    const task = result.value;
    const existingIndex = dbTasks.findIndex(t => t.id === task.id);
    if (existingIndex !== -1) {
      const existingPublishedIndex = dbTasksPublished.findIndex(t => t.id === task.id);
      dbTasks[existingIndex] = applyStateTransition(dbTasksPublished, existingPublishedIndex, result.meta, task, task.state, (itm, newState) => {
        itm.state = newState;
        return itm;
      });
    } else {
      task.translationKey = appender('predefinedTasks', task.name);
      dbTasks.push(task);
    }
    return Result.ok(task);
  };

  return builder
    .intercept(new GetPredefinedTask().handle(async (input, ctx) => {
      return getTask(ctx);
    }))
    .intercept(new SearchPredefinedTask().handle(async (input: SearchInput, ctx) => {
      const filtered = dbTasks
        .slice(input.offset, input.offset + input.limit)
        .filter(t =>
          t.name.toLowerCase().includes(input.query.toLowerCase()) ||
          (t.type && t.type.toLowerCase().includes(input.query.toLowerCase())) ||
          (t.translationKey && t.translationKey.toLowerCase().includes(input.query.toLowerCase()))
        );
      return Result.ok(filtered);
    }))
    .intercept(new CreatePredefinedTask().handle(async (input: CreatePredefinedTaskInput, ctx) => {
      const newTask: PredefinedTask = {
        id: input.predefinedTaskId ?? generateUUID(),
        type: input.type,
        name: input.name,
        species: [],
        suggestedReminders: [],
        iconName: undefined,
        translationKey: '',
        state: { state: 'draft', isDirty: false }
      };
      return saveTask(Result.ok(newTask), ctx);
    }))
    .intercept(new DeletePredefinedTask().handle(async (input, ctx) => {
      const id = ctx.getPathParameter<string>('predefinedTaskId');
      const idx = dbTasks.findIndex(t => t.id === id);
      if (idx === -1) {
        return Result.notFound('Backoffice.Tasks.NotFound', `PredefinedTask with id ${id} not found`);
      }
      dbTasks.splice(idx, 1);
      return Result.ok(undefined);
    }))
    .intercept(new UpdatePredefinedTaskType().handle(async (input: UpdatePredefinedTaskTypeInput, ctx) => {
      return saveTask(getTask(ctx)
        .maybe((value, meta) => {
          value.type = input.type;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdatePredefinedTaskName().handle(async (input: UpdatePredefinedTaskNameInput, ctx) => {
      return saveTask(getTask(ctx)
        .maybe((value, meta) => {
          value.name = input.name;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdatePredefinedTaskIcon().handle(async (input: UpdatePredefinedTaskIconInput, ctx) => {
      return saveTask(getTask(ctx)
        .maybe((value, meta) => {
          value.iconName = input.icon;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdatePredefinedTaskSuggestedRRULE().handle(async (input: UpdatePredefinedTaskSuggestedRRULEInput, ctx) => {
      return saveTask(getTask(ctx)
        .maybe((value, meta) => {
          value.suggestedRRULE = input.suggestedRRULE;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    // Add missing interceptors for species operations
    .intercept(new AddPredefinedTaskSpecies().handle(async (input, ctx) => {
      return saveTask(getTask(ctx)
        .maybe((value, meta) => {
          // Add or update species entry
          const idx = value.species.findIndex(s => s.speciesId === input.speciesId);
          if (idx === -1) {
            value.species.push({ speciesId: input.speciesId, breedIds: input.breedId ?? [] });
          } else {
            value.species[idx].breedIds = input.breedId ?? [];
          }
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdatePredefinedTaskSpecies().handle(async (input, ctx) => {
      return saveTask(getTask(ctx)
        .maybe((value, meta) => {
          // Update breedIds for existing species entry
          const idx = value.species.findIndex(s => s.speciesId === input.speciesId);
          if (idx !== -1) {
            value.species[idx].breedIds = input.breedId ?? [];
          }
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new RemovePredefinedTaskSpecies().handle(async (input, ctx) => {
      return saveTask(getTask(ctx)
        .maybe((value, meta) => {
          // Remove species entry
          value.species = value.species.filter(s => s.speciesId !== input.speciesId);
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdatePredefinedTaskState().handle(async (input, ctx) => {
      return saveTask(getTask(ctx)
        .maybe((value, meta) => {
          value.state = transitionState(meta, value.state, input.stateTransition);
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }));
}
